// ─────────────────────────────────────────────────────────────────────────────────────────────
// empresa.js — el cliente del ESPEJO: PQR, técnicos y proveedores viven en `alebas-gestion`.
//
// 🔑 Acá NO hay caché ni `localStorage`, y es a propósito. El resto de los servicios de este repo
// hidratan una copia local (`sh:entitlements`, `al:ordenes`) porque su dueño es Solar Hub. El dueño
// de estos tres es la gestión de la empresa: una copia local sería una segunda verdad que se
// desincroniza en silencio el día que el root cierre una PQR desde `app.alebas.co`.
//
// 🔴 Por eso tampoco hay degradación a caché: si el puente no responde, se DICE. Una lista vacía
// se lee como «no hay quejas», que es la peor respuesta posible.
//
// El token de administración viaja como manda cada verbo: en el `Authorization` para los GET (no
// hay cuerpo donde meterlo) y en el cuerpo para los POST, igual que `entitlements` y `ordenes`.

import { hubGet, hubPost, hubConfigurado } from './hub';
import { tokenParaBackend } from './adminAuth';

const sessionToken = () => tokenParaBackend();

/** Cabecera de los GET gateados por `exigeAdmin`. */
function auth() {
  const t = sessionToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

export const empresaBackendConfigured = () => hubConfigurado();

/** ¿Está configurado el puente con la gestión? Sin llave, responde `configurado:false` con motivo. */
export async function estadoDelPuente() {
  return hubGet('empresa/estado');
}

// ─────────────────────────────────────── PQR ───────────────────────────────────────

export async function listarPqr({ estado, vencidas } = {}) {
  return hubGet('empresa/pqr', { estado, vencidas: vencidas ? '1' : undefined }, auth());
}

export async function verPqr(radicado) {
  return hubGet(`empresa/pqr/${encodeURIComponent(radicado)}`, {}, auth());
}

/**
 * Radica la que llegó por otro canal (teléfono, correo, en la tienda).
 *
 * 🔴 `linea` NO se manda: allá la impone la llave. Mandarla sería invitar a radicar una queja a
 * nombre de otra línea, con el término legal de otro régimen.
 */
export async function radicarPqr(datos) {
  return hubPost('empresa/pqr', { ...datos, token: sessionToken() });
}

export async function direccionarPqr(radicado, { area, responsable }) {
  return hubPost(`empresa/pqr/${encodeURIComponent(radicado)}/direccionar`, { area, responsable, token: sessionToken() });
}

/** Responder. La gestión además se la MANDA a quien radicó: una respuesta que no sale no es respuesta. */
export async function responderPqr(radicado, respuesta) {
  return hubPost(`empresa/pqr/${encodeURIComponent(radicado)}/responder`, { respuesta, token: sessionToken() });
}

export async function prorrogarPqr(radicado, { hasta, motivo }) {
  return hubPost(`empresa/pqr/${encodeURIComponent(radicado)}/prorrogar`, { hasta, motivo, token: sessionToken() });
}

/** Cerrar. La gestión rechaza cerrar sin haber respondido: eso sería cerrar un incumplimiento. */
export async function cerrarPqr(radicado, { motivo, desistida } = {}) {
  return hubPost(`empresa/pqr/${encodeURIComponent(radicado)}/cerrar`, { motivo, desistida, token: sessionToken() });
}

// ───────────────────────────────────── TÉCNICOS ─────────────────────────────────────

/** El padrón de la suite. `mios` lo acota a los de Solar Hub. */
export async function listarTecnicos({ mios, busca, estado } = {}) {
  return hubGet('empresa/tecnicos', { mios: mios ? '1' : undefined, busca, estado }, auth());
}

/** La ficha: dice además si ya está inscrito como contratista y si se le PUEDE GIRAR. */
export async function verTecnico(documento) {
  return hubGet(`empresa/tecnicos/${encodeURIComponent(documento)}`, {}, auth());
}

/**
 * Alta en el padrón. Idempotente por documento y NO pisa la ficha existente.
 *
 * 🔴 Si la persona pertenece a un OPERADOR, la gestión rechaza con 422: ese técnico lo contrata,
 * lo suspende y le paga el operador. El mensaje se muestra tal cual en vez de tragárselo.
 */
export async function altaTecnico(datos) {
  return hubPost('empresa/tecnicos', { ...datos, token: sessionToken() });
}

/** Sacarlo (o volver a meterlo) en Solar Hub. No lo borra de la suite. */
export async function tecnicoEnLinea(documento, activo) {
  return hubPost(`empresa/tecnicos/${encodeURIComponent(documento)}/linea`, { activo, token: sessionToken() });
}

// ─────────────────────────────────── PROVEEDORES ───────────────────────────────────
//
// 🪤 «Proveedor» acá ya significaba otra cosa: `SuppliersMgr` es el PORTAL del proveedor de la
// tienda (stock, órdenes de compra). Esto es el PADRÓN DE TERCEROS de la empresa, que es donde se
// decide si a alguien se le puede pagar. Son dos cosas distintas y viven en servicios distintos.

export async function listarProveedores({ busca, estado } = {}) {
  return hubGet('empresa/proveedores', { busca, estado }, auth());
}

/**
 * EL CRUCE de técnicos: quién trabaja para Solar Hub y la empresa no lo tiene —o no ha dicho a
 * dónde le giran, que es lo que hay que arreglar ANTES de que haya una obra cerrada esperándole.
 */
export async function cruceTecnicos() {
  return hubGet('empresa/tecnicos/cruce', {}, auth());
}

/**
 * EL CRUCE: a quién le compra la Tienda y no está en el padrón de la empresa. No inscribe —eso es
 * del root— : dice quién falta, que es lo que no se sabía en ningún lado.
 */
export async function cruceProveedores() {
  return hubGet('empresa/proveedores/cruce', {}, auth());
}

export async function verProveedor(nit) {
  return hubGet(`empresa/proveedores/${encodeURIComponent(nit)}`, {}, auth());
}
